import { type Analysis } from './analyze';
import { catOf, type PartLookup } from './catalog';
import { t as $t } from './i18n';
import { barPitch, barSupports, barThickness, busbarAmps, busbarDims, layoutDevices, layoutIssues, PHASE_CLEAR } from './layout';
import { panel3dIssues } from './panel3d';
import type { CheckMessage } from './reports';
import type { Project } from './types';

/*
 * Design verification of a low-voltage board after IEC 61439-1/-2 clause 10: what the drawing and the layout
 * can show is checked here (busbar sizes against the largest switching device, the protective conductor, phase
 * clearances, the protective circuit, the devices and the internal circuits); what needs test results or the
 * manufacturer's data (temperature rise of the devices, short-circuit withstand, dielectric tests, IP) is listed
 * as to be verified, never assumed.
 */

export type VerifyStatus = 'ok' | 'check' | 'fail' | 'verify';
export interface VerifyRow {
  clause: string;
  topic: string;
  status: VerifyStatus;
  detail: string;
  pi?: number;
}

/** Minimum clearance in air for the rated impulse withstand voltage of a 400/230 V board, Uimp 8 kV (IEC 61439-1 Table 1). */
export const CLEARANCE_8KV = 8;

/** Smallest copper protective conductor for a phase conductor of S mm² (IEC 61439-1 Table 5). */
export function peRequired(s: number): number {
  return s <= 16 ? s : s <= 35 ? 16 : s <= 400 ? s / 2 : s <= 800 ? 200 : s / 4;
}
const area = (spec: string) => {
  const d = busbarDims(spec);
  return d.n * d.w * d.t;
};

/** `checks`: the project's check run (the messages the user did not ignore). */
export function boardVerification(pr: Project, A: Analysis, parts: PartLookup = catOf, checks: CheckMessage[] = []): VerifyRow[] {
  const layouts = pr.pages.map((p, pi) => ({ p, pi })).filter(({ p }) => p.kind === 'layout' && p.layout && !p.layout.door && !p.layout.inside);
  if (!layouts.length) return [];
  const out: VerifyRow[] = [];
  const devs = layoutDevices(A, parts);
  const issues = layoutIssues(pr, A, parts);
  const nf = (v: number) => String(Math.round(v));

  for (const { p, pi } of layouts) {
    const l = p.layout!;
    const bars = l.bars ?? [];
    bars.forEach((bar, k) => {
      // the largest switching device along this busbar: what it carries at most
      const big = l.items
        .filter((i) => i.x + (devs.get(i.dev)?.w ?? 0) / 2 >= bar.x && i.x + (devs.get(i.dev)?.w ?? 0) / 2 <= bar.x + bar.w)
        .map((i) => devs.get(i.dev))
        .filter((d) => d?.amps)
        .sort((a, b) => b!.amps! - a!.amps!)[0];
      const which = bars.length > 1 ? ` · ${$t('bara {0}', k + 1)}` : '';
      const cap = busbarAmps(bar.spec);
      if (big?.amps && cap != null)
        out.push({
          clause: '10.10', topic: $t('Ana bara akım taşıma') + which, pi,
          status: cap >= big.amps ? 'ok' : 'fail',
          detail: $t('Faz baraları Cu {0}: {1} A (DIN 43671, boyalı, 35 °C); en büyük şalter {2}: {3} A', bar.spec, cap, big.name, big.amps),
        });
      if (bar.n && big?.amps) {
        const capN = busbarAmps(bar.n);
        out.push({
          clause: '10.10', topic: $t('Nötr barası') + which, pi, status: 'check',
          detail: $t('Yarım kesit nötr Cu {0}: {1} A; harmonik akımlı (doğrusal olmayan) yüklerde nötr faz kadar yüklenebilir', bar.n, capN ?? '?'),
        });
      }
      const need = peRequired(area(bar.spec)), has = area(bar.pe);
      out.push({
        clause: '10.5', topic: $t('Koruma iletkeni (PE) barası') + which, pi,
        status: has >= need ? 'ok' : 'fail',
        detail: $t('PE Cu {0}: {1} mm²; faz {2} mm² için en az {3} mm² (IEC 61439-1 Tablo 5)', bar.pe, nf(has), nf(area(bar.spec)), nf(need)),
      });
      const gap = barPitch(bar.spec) - barThickness(bar.spec);
      out.push({
        clause: '10.4', topic: $t('Fazlar arası hava aralığı') + which, pi,
        status: Math.min(gap, PHASE_CLEAR) >= CLEARANCE_8KV ? 'ok' : 'fail',
        detail: $t('Fazlar arası en az {0} mm; Uimp 8 kV için en az {1} mm (IEC 61439-1 Tablo 1). Yüzeysel kaçak yollarını mesnet üreticisinin verisine göre doğrulayın.', nf(Math.min(gap, PHASE_CLEAR)), CLEARANCE_8KV),
      });
    });
    // copper on breaker poles closer than the phase clearance
    const tight = issues.filter((m) => m.pi === pi && m.msg.includes(String(PHASE_CLEAR)) && /bakır|copper/i.test(m.msg));
    if (tight.length) out.push({ clause: '10.4', topic: $t('Şalter uçlarında bakır aralığı'), pi, status: 'check', detail: tight.map((m) => m.msg).join(' · ') });
    // supports: the longest unsupported run, to compare with the busbar system's short-circuit table
    const sup = barSupports(l);
    if (sup.length) {
      let span = 0;
      for (const bar of l.bars ?? []) {
        const xs = sup.filter((s) => s.bar === bar).map((s) => s.x).sort((a, b) => a - b);
        for (let k = 1; k < xs.length; k++) span = Math.max(span, xs[k] - xs[k - 1]);
      }
      out.push({
        clause: '10.11', topic: $t('Kısa devre dayanımı'), pi, status: 'verify',
        detail: $t('En büyük mesnet aralığı {0} mm. İzin verilen aralığı ve Icw değerini bara sistemi üreticisinin kısa devre tablosundan doğrulayın.', nf(span)),
      });
    }
    const heavy = issues.filter((m) => m.pi === pi && /kg/.test(m.msg));
    out.push({ clause: '10.2', topic: $t('Göz yükleri'), pi, status: heavy.length ? 'check' : 'ok', detail: heavy.length ? heavy.map((m) => m.msg).join(' · ') : $t('Her gözün cihaz ağırlığı sınırın altında.') });
  }

  // the whole project: protective circuit, devices, internal circuits, cabinet
  const pe = checks.filter((m) => m.rule?.includes('PE'));
  out.push({ clause: '10.5', topic: $t('Koruma devresinin sürekliliği'), status: pe.some((m) => m.sev === 'err') ? 'fail' : pe.length ? 'check' : 'ok', detail: pe.length ? pe.map((m) => m.msg).join(' · ') : $t('Bütün PE uçları koruma iletkenine bağlı.') });
  const placed = layouts.flatMap(({ p }) => p.layout!.items.map((i) => devs.get(i.dev))).filter(Boolean);
  const unsized = placed.filter((d) => !d!.sized);
  const placing = issues.filter((m) => m.sev === 'err');
  out.push({
    clause: '10.6', topic: $t('Cihazların yerleştirilmesi'), status: placing.length ? 'fail' : unsized.length ? 'check' : 'ok',
    detail: placing.length ? placing.map((m) => m.msg).join(' · ') : unsized.length ? $t('Parça ölçüsü olmayan cihazlar: {0}', unsized.map((d) => d!.name).join(', ')) : $t('{0} cihaz parça ölçüleriyle yerleştirildi, çakışma yok.', placed.length),
  });
  const err = checks.filter((m) => m.sev === 'err'), warn = checks.filter((m) => m.sev === 'warn');
  out.push({ clause: '10.7', topic: $t('İç devreler ve bağlantılar'), status: err.length ? 'fail' : warn.length ? 'check' : 'ok', detail: err.length || warn.length ? $t('Kontrol: {0} hata, {1} uyarı (Kontrol sekmesi).', err.length, warn.length) : $t('Kontrol temiz: kesit–koruma uyumu, bağlantı noktası başına tel sayısı, bağlantısız uç yok.') });
  const cab = panel3dIssues(pr, A, parts);
  out.push({ clause: '10.6', topic: $t('Kabin derinliği ve kapak'), status: cab.some((m) => m.sev === 'err') ? 'fail' : cab.length ? 'check' : 'ok', detail: cab.length ? cab.map((m) => m.msg).join(' · ') : $t('Cihazlar kabin derinliğine sığıyor, kapak cihazlarıyla çakışma yok.') });
  // what the drawing cannot show
  const todo: [string, string, string][] = [
    ['10.10', $t('Sıcaklık artışı'), $t('Cihaz güç kayıpları parça veritabanında yok: IEC TR 60890 hesabı (≤ 1600 A) ya da test / üreticinin doğrulanmış tasarımı ile gösterin.')],
    ['10.9', $t('Dielektrik özellikler'), $t('Güç frekansı dayanım ve darbe gerilimi doğrulaması (test ya da üretici beyanı).')],
    ['10.3', $t('Koruma derecesi (IP)'), $t('Kabin ve kapak kesimleri için IP derecesini üretici beyanıyla doğrulayın.')],
    ['10.2', $t('Malzeme dayanımı'), $t('Kabin, mesnet ve yalıtkanların dayanımı: üretici beyanı.')],
    ['10.12', $t('EMC'), $t('Elektronik cihazların (sürücü, kontrolör) üretici EMC beyanları.')],
    ['10.13', $t('Mekanik çalışma'), $t('Şalter kolları, kilitler ve kapakların çalışma testi.')],
  ];
  for (const [clause, topic, detail] of todo) out.push({ clause, topic, status: 'verify', detail });
  return out;
}
